// "What clients see" (BA P1.6a catalogue, provider view): the company's own live
// Brand Ambassador profiles exactly as the catalogue serves them (commerce P1.3).
// Owner AC3: it renders ONLY the catalogue's client-view responses — no
// employee data, nothing merged client-side. In the pilot, coordinators open
// this and present it to the client; clients don't sign in (decision D), so
// there is no buyer entry point here. Presentation only — commerce enforces
// the permission, the flag and the provider's entitlement.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { useAppSelector } from '@/app/hooks';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Badge } from '@/features/shared/components/ui/badge';
import { DataState } from '@/features/shared/components/ui/DataState';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  BaCatalogueFilters, BaClientView, getBaCatalogueProfile, listBaCatalogue,
} from '@/services/baProfileService';
import { ClientProfileView } from '../components/ClientProfileView';

type Load = { s: 'loading' } | { s: 'ok'; rows: BaClientView[]; next?: string } | { s: 'denied' } | { s: 'unavailable' } |
  { s: 'error'; msg: string };

export const WhatClientsSeePage: React.FC = () => {
  const { t } = useTranslation('brandAmbassadors');
  const company = useAppSelector((s) => s.tenant?.current?.name) || t('company_fallback', { defaultValue: 'your company' });
  const [draft, setDraft] = useState<BaCatalogueFilters>({});
  const [filters, setFilters] = useState<BaCatalogueFilters>({});
  const [list, setList] = useState<Load>({ s: 'loading' });
  const [more, setMore] = useState(false);
  const [open, setOpen] = useState<BaClientView | null>(null);

  const fail = useCallback((e: any): Load => {
    const st = e?.response?.status;
    if (st === 403) return { s: 'denied' };
    if (st === 404) return { s: 'unavailable' };
    return { s: 'error', msg: getApiErrorMessage(e, t('errors.catalogue', { defaultValue: 'Could not load the catalogue.' }) as string) };
  }, [t]);

  const load = useCallback(() => {
    setList({ s: 'loading' });
    setOpen(null);
    listBaCatalogue(filters).then((r) => setList({ s: 'ok', rows: r.profiles, next: r.next })).catch((e) => setList(fail(e)));
  }, [filters, fail]);
  useEffect(load, [load]);

  const loadMore = async () => {
    if (list.s !== 'ok' || !list.next) return;
    setMore(true);
    try {
      const r = await listBaCatalogue(filters, list.next);
      setList({ s: 'ok', rows: [...list.rows, ...r.profiles], next: r.next });
    } catch (e) { setList(fail(e)); } finally { setMore(false); }
  };

  const show = (id: string) =>
    getBaCatalogueProfile(id).then(setOpen).catch((e) => setList(fail(e)));

  const field = (k: keyof BaCatalogueFilters, label: string) => (
    <Input value={draft[k] ?? ''} placeholder={label} aria-label={label} className="w-44"
      onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} />
  );

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div>
        <h1 className="text-h2 text-foreground">{t('catalogue.title', { defaultValue: 'What clients see' })}</h1>
        <p className="text-sm text-muted-foreground">
          {t('catalogue.subtitle', { defaultValue: '{{company}}’s published Brand Ambassador profiles, exactly as the catalogue shows them to clients.', company })}
        </p>
      </div>
      <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); setFilters(draft); }}>
        {field('skill', t('fields.skill_filter', { defaultValue: 'Skill' }) as string)}
        {field('language', t('fields.language_filter', { defaultValue: 'Language' }) as string)}
        {field('service_area', t('fields.area_filter', { defaultValue: 'Service area' }) as string)}
        <Button type="submit" variant="outline"><Search className="w-4 h-4 mr-1" />{t('actions.search', { defaultValue: 'Search' })}</Button>
      </form>

      <div className="grid gap-4 lg:grid-cols-[22rem_1fr]">
        <Card><CardContent className="p-0">
          {list.s === 'loading' && <DataState variant="loading" />}
          {list.s === 'denied' && <DataState variant="denied" />}
          {list.s === 'unavailable' && (
            <DataState variant="unavailable" message={t('catalogue.unavailable', { defaultValue: 'The Brand Ambassador catalogue isn’t available for this company.' }) as string} />
          )}
          {list.s === 'error' && <DataState variant="error" message={list.msg} onRetry={load} />}
          {list.s === 'ok' && list.rows.length === 0 && (
            <DataState variant="empty" title={t('catalogue.empty', { defaultValue: 'No published profiles match.' }) as string} />
          )}
          {list.s === 'ok' && list.rows.length > 0 && (
            <>
              <ul>{list.rows.map((v) => (
                <li key={v.profileId}>
                  <button type="button" onClick={() => show(v.profileId)}
                    className={`w-full text-left px-4 py-3 border-b border-border hover:bg-muted ${open?.profileId === v.profileId ? 'bg-muted' : ''}`}>
                    <p className="text-sm font-medium text-foreground">{v.content?.display_name || '—'}</p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(v.content?.skills ?? []).slice(0, 4).map((s) => <Badge key={s} variant="outline">{s}</Badge>)}
                    </div>
                  </button>
                </li>
              ))}</ul>
              {list.next && (
                <div className="p-3 text-center">
                  <Button variant="outline" size="sm" disabled={more} onClick={loadMore}>
                    {t('actions.load_more', { defaultValue: 'Load more' })}
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent></Card>
        <Card><CardContent className="p-4">
          {open
            ? <ClientProfileView view={open} source="published" company={company} />
            : <DataState variant="empty" title={t('catalogue.pick', { defaultValue: 'Pick a profile to see it as a client does.' }) as string} />}
        </CardContent></Card>
      </div>
    </div>
  );
};

export default WhatClientsSeePage;
